'use client';

import { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { BookOpen, Clock, CheckCircle, AlertCircle } from 'lucide-react';

interface Test {
  id: string;
  title: string;
  description: string | null;
  type: string;
}

interface Survey {
  id: string;
  title: string;
  description: string | null;
}

interface PreEventContentProps {
  eventId: string;
  eventStartDate: string | null;
  enrollmentId: string;
  /** False when the enrollment's ticket needs no test (no certificate, or a speaker ticket). */
  showTests: boolean;
}

export default function PreEventContent({ eventId, eventStartDate, enrollmentId, showTests }: PreEventContentProps) {
  const { t } = useLanguage();
  const [preEventTests, setPreEventTests] = useState<Test[]>([]);
  const [preEventSurveys, setPreEventSurveys] = useState<Survey[]>([]);
  const [completedTests, setCompletedTests] = useState<Set<string>>(new Set());
  const [completedSurveys, setCompletedSurveys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    fetchPreEventContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchPreEventContent = async () => {
    setLoading(true);

    // Fetch tests that are NOT flagged as available_after_event_end
    const { data: tests } = await supabase
      .from('tests')
      .select('*')
      .eq('content_id', eventId)
      .or('available_after_event_end.is.null,available_after_event_end.eq.false')
      .order('sort_order');

    if (tests) {
      setPreEventTests(tests);

      // Check completed tests
      const { data: testAttempts } = await supabase
        .from('test_attempts')
        .select('test_id, passed')
        .eq('enrollment_id', enrollmentId)
        .eq('passed', true);

      if (testAttempts) {
        setCompletedTests(new Set(testAttempts.map(a => a.test_id)));
      }
    }

    // Fetch surveys that are NOT flagged as available_after_event_end
    const { data: surveys } = await supabase
      .from('surveys')
      .select('*')
      .eq('content_id', eventId)
      .or('available_after_event_end.is.null,available_after_event_end.eq.false')
      .order('sort_order');

    if (surveys) {
      setPreEventSurveys(surveys);

      // Check completed surveys
      const { data: surveyResponses } = await supabase
        .from('survey_responses')
        .select('survey_id')
        .eq('enrollment_id', enrollmentId);

      if (surveyResponses) {
        setCompletedSurveys(new Set(surveyResponses.map(r => r.survey_id)));
      }
    }

    setLoading(false);
  };

  const getDaysUntilEvent = () => {
    if (!eventStartDate) return null;
    const days = Math.ceil((new Date(eventStartDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const visibleTests = showTests ? preEventTests : [];
  const hasPreEventContent = visibleTests.length > 0 || preEventSurveys.length > 0;
  const daysUntilEvent = getDaysUntilEvent();

  if (!hasPreEventContent) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border-2 border-blue-200 p-6 mb-8">
      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <div className="bg-blue-100 rounded-full p-3">
          <BookOpen className="h-6 w-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('event.prepareForEvent')}
          </h2>
          <p className="text-gray-600">
            {t('event.completeBeforeEvent')}
          </p>
          {daysUntilEvent !== null && (
            <div className="flex items-center gap-2 mt-2 text-blue-600">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-semibold">
                {daysUntilEvent} {t('event.daysUntilEvent')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Pre-Event Tests */}
      {visibleTests.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            {t('event.preEvent.testsTitle')}
          </h3>
          <div className="space-y-3">
            {visibleTests.map((test) => {
              const isCompleted = completedTests.has(test.id);
              return (
                <div
                  key={test.id}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                    isCompleted
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{test.title}</p>
                      {test.description && (
                        <p className="text-sm text-gray-600">{test.description}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {test.type === 'pre_assessment' ? t('event.preEvent.preAssessmentBadge') : 'Knowledge Check'}
                      </p>
                    </div>
                  </div>
                  {isCompleted && (
                    <span className="text-sm font-semibold text-green-700 ml-3">
                      {t('common.completed')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pre-Event Surveys */}
      {preEventSurveys.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            {t('event.preEvent.surveysTitle')}
          </h3>
          <div className="space-y-3">
            {preEventSurveys.map((survey) => {
              const isCompleted = completedSurveys.has(survey.id);
              return (
                <div
                  key={survey.id}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                    isCompleted
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{survey.title}</p>
                      {survey.description && (
                        <p className="text-sm text-gray-600">{survey.description}</p>
                      )}
                    </div>
                  </div>
                  {isCompleted && (
                    <span className="text-sm font-semibold text-green-700 ml-3">
                      {t('common.completed')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-600 text-center">
          Complete the materials above in the Tests and Surveys tabs below
        </p>
      </div>
    </div>
  );
}
